import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { Icon, Link, Text, tokens } from '@hotjar/hotsauce';
import type { IconName } from '@hotjar/hotsauce';
import type {
  Categories,
  FeedbackData,
  FeedbackSentiment,
} from '@hotjar/webapp/domains/activation/featured-insights/types';

const ImageContainer = styled(Link)`
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: 350px;
  max-height: 175px;
  margin: 18px auto 0;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x0pt5};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const FeedbackContainer = styled.div`
  flex: 1;
  padding: ${tokens.spacer.x4pt5} ${tokens.spacer.x5};
`;

const feedbackIconSized = {
  s: tokens.spacer.x4,
  m: tokens.spacer.x7,
  l: '120px',
};

const FeedbackIcon = styled(Icon)<{ iconSize?: 's' | 'm' | 'l' }>`
  width: ${({ iconSize }) => feedbackIconSized[iconSize || 's']};
  min-width: ${({ iconSize }) => feedbackIconSized[iconSize || 's']};
  height: auto;
  margin-right: ${tokens.spacer.x2};
`;

const FeedbackQuoteRowContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 60px;
`;

const FeedbackQuoteColContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${tokens.spacer.x3};
  height: 100%;
  padding: 0 20px;

  & ${FeedbackIcon} {
    margin-right: 0;
  }
`;

const FeedbackQuote = styled(Text)`
  ${tokens.text('subtitle1')};
  font-weight: 400;
  font-style: italic;
`;

const FeedbackIconContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.spacer.x0pt5};

  & ${FeedbackIcon} {
    margin-right: 0;
  }
`;

const FeedbackLabel = styled(FeedbackQuote)<{ bigLabel?: boolean }>`
  display: block;
  color: ${tokens.color.text_light};

  ${({ bigLabel }) =>
    bigLabel
      ? `
      margin-top: -5px;
      font-size: 20px;
    `
      : ``}
`;

const getSentiment = (score: number): FeedbackSentiment => {
  if (score === 4) return 'Love';
  if (score === 3) return 'Like';
  if (score === 2) return 'Neutral';
  if (score === 1) return 'Dislike';
  return 'Hate';
};
type IconColor = keyof typeof tokens.ALIAS_TEXT_COLORS;
const emotionsList: { color: IconColor; iconName: IconName }[] = [
  {
    color: 'text_warning',
    iconName: 'hate_alt',
  },
  {
    color: 'tangerine_light' as IconColor,
    iconName: 'dislike_alt',
  },
  {
    color: 'ui_border' as IconColor,
    iconName: 'neutral_alt',
  },
  {
    color: 'mint_600' as IconColor,
    iconName: 'like_alt',
  },
  {
    color: 'text_success',
    iconName: 'love_alt',
  },
];

const Feedback = ({
  rating,
  description,
  hasImage,
}: {
  rating: FeedbackData['emotion'];
  description: FeedbackData['message'];
  hasImage: boolean;
}) => {
  if (!hasImage && !description?.length) {
    return (
      <FeedbackQuoteColContainer>
        <FeedbackIcon
          icon={emotionsList?.[rating]?.iconName}
          color={emotionsList?.[rating]?.color}
          iconSize="l"
        />
        <FeedbackLabel bigLabel>{getSentiment(rating)}</FeedbackLabel>
      </FeedbackQuoteColContainer>
    );
  }

  if (!description?.length) {
    return (
      <FeedbackIconContainer>
        <FeedbackIcon
          icon={emotionsList?.[rating]?.iconName}
          color={emotionsList?.[rating]?.color}
          iconSize="m"
        />
        <FeedbackLabel>{getSentiment(rating)}</FeedbackLabel>
      </FeedbackIconContainer>
    );
  }

  if (!hasImage) {
    return (
      <FeedbackQuoteColContainer>
        <FeedbackIcon
          icon={emotionsList?.[rating]?.iconName}
          color={emotionsList?.[rating]?.color}
          iconSize="l"
        />
        <FeedbackQuote>{`"${description}"`}</FeedbackQuote>
      </FeedbackQuoteColContainer>
    );
  }

  return (
    <FeedbackQuoteRowContainer>
      <FeedbackIcon icon={emotionsList?.[rating]?.iconName} color={emotionsList?.[rating]?.color} />
      <FeedbackQuote>{`"${description}"`}</FeedbackQuote>
    </FeedbackQuoteRowContainer>
  );
};

export const FeedbackMainSection = ({
  category,
  data,
  onClick,
}: {
  category: Categories;
  data: FeedbackData;
  onClick: (slideId: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <FeedbackContainer>
      <Feedback
        rating={data?.emotion}
        description={data?.message}
        hasImage={!!data?.imageUrl?.length}
      />
      {data?.imageUrl ? (
        <ImageContainer
          href={data?.responseUrl}
          title={t('activation:space-overview.for-you.featured-insights.thumbnail-alt-feedback')}
          kind="standalone"
          onClick={() => onClick(category)}
          data-testid="slide-image-cta-feedback"
        >
          {(<Image src={data?.imageUrl} />) as string & React.ReactNode}
        </ImageContainer>
      ) : null}
    </FeedbackContainer>
  );
};
